import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { amplifyConfig } from '@/config/env';
import { useAuthStore, type AuthResult } from '@/stores/authStore';
import type { AuthenticatedUser, UserCredentials } from '@/services/auth';

Amplify.configure(amplifyConfig as ResourcesConfig);

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthenticatedUser | null;
  signIn: (credentials: UserCredentials) => Promise<AuthResult>;
  confirmNewPassword: (newPassword: string) => Promise<AuthResult>;
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    signIn,
    confirmNewPassword,
    requestPasswordReset,
    confirmPasswordReset,
    signOut,
    checkAuth,
    clearError,
  } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    confirmNewPassword,
    requestPasswordReset,
    confirmPasswordReset,
    signOut,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
